import axios from 'axios';
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE = '/api/v1';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE,
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
    });

    this.client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('accessToken');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('accessToken');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      },
    );
  }

  // ─── Auth ───
  async login(identifier: string, password: string) {
    const res = await this.client.post('/auth/login', { identifier, password });
    return res.data.data;
  }

  async logout() {
    return this.client.post('/auth/logout');
  }

  async forgotPassword(identifier: string) {
    const res = await this.client.post('/auth/forgot-password', { identifier });
    return res.data.data;
  }

  async resetPassword(token: string, newPassword: string) {
    const res = await this.client.post('/auth/reset-password', { token, newPassword });
    return res.data.data;
  }

  async getMe() {
    const res = await this.client.get('/auth/me');
    return res.data.data;
  }

  async getSessions() {
    const res = await this.client.get('/auth/sessions');
    return res.data.data;
  }

  async getMyScope() {
    const res = await this.client.get('/auth/me/scope');
    return res.data.data;
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.client.post('/auth/change-password', { currentPassword, newPassword });
  }

  async revokeSession(sessionId: string) {
    return this.client.delete(`/auth/sessions/${sessionId}`);
  }

  async refreshToken() {
    const res = await this.client.post('/auth/refresh');
    const data = res.data.data;
    if (data.accessToken) {
      localStorage.setItem('accessToken', data.accessToken);
    }
    return data;
  }

  async logoutAll() {
    return this.client.post('/auth/logout-all');
  }

  // ─── Users ───
  async listUsers(params?: Record<string, string>) {
    const res = await this.client.get('/users', { params });
    return res.data;
  }

  async getUser(userId: string) {
    const res = await this.client.get(`/users/${userId}`);
    return res.data.data;
  }

  async createUser(data: Record<string, unknown>) {
    const res = await this.client.post('/users', data);
    return res.data.data;
  }

  async quickCreateUser(data: Record<string, unknown>) {
    const res = await this.client.post('/users/quick-create', data);
    return res.data.data;
  }

  async updateUser(userId: string, data: Record<string, unknown>) {
    const res = await this.client.put(`/users/${userId}`, data);
    return res.data.data;
  }

  async resetUserPassword(userId: string) {
    const res = await this.client.post(`/users/${userId}/reset-password`);
    return res.data.data;
  }

  async activateUser(userId: string) {
    const res = await this.client.post(`/users/${userId}/activate`);
    return res.data.data;
  }

  async deactivateUser(userId: string) {
    const res = await this.client.post(`/users/${userId}/deactivate`);
    return res.data.data;
  }

  async unlockUser(userId: string) {
    const res = await this.client.post(`/users/${userId}/unlock`);
    return res.data.data;
  }

  async updateUserRoles(userId: string, roles: string[]) {
    const res = await this.client.put(`/users/${userId}/roles`, { roles });
    return res.data.data;
  }

  // ─── Students ───
  async listStudents(params?: Record<string, string>) {
    const res = await this.client.get('/students', { params });
    return res.data;
  }

  async getStudent(studentId: string) {
    const res = await this.client.get(`/students/${studentId}`);
    return res.data.data;
  }

  async createStudent(data: Record<string, unknown>) {
    const res = await this.client.post('/students', data);
    return { student: res.data.data, generatedCredentials: res.data.generatedCredentials ?? [] };
  }

  async updateStudent(studentId: string, data: Record<string, unknown>) {
    const res = await this.client.put(`/students/${studentId}`, data);
    return res.data.data;
  }

  async getStudentEnrollments(studentId: string) {
    const res = await this.client.get(`/students/${studentId}/enrollments`);
    return res.data.data;
  }

  async enrollStudent(studentId: string, data: Record<string, unknown>) {
    const res = await this.client.post(`/students/${studentId}/enroll`, data);
    return res.data.data;
  }

  async promoteStudents(data: Record<string, unknown>) {
    const res = await this.client.post('/students/promote', data);
    return res.data.data;
  }

  async transferStudent(studentId: string, data: Record<string, unknown>) {
    const res = await this.client.post(`/students/${studentId}/transfer`, data);
    return res.data.data;
  }

  async withdrawStudent(studentId: string, data: Record<string, unknown>) {
    const res = await this.client.post(`/students/${studentId}/withdraw`, data);
    return res.data.data;
  }

  async uploadStudentDocument(studentId: string, data: Record<string, unknown>) {
    const res = await this.client.post(`/students/${studentId}/documents`, data);
    return res.data.data;
  }

  async getStudentDocuments(studentId: string) {
    const res = await this.client.get(`/students/${studentId}/documents`);
    return res.data.data;
  }

  async getStudentLifecycleEvents(studentId: string) {
    const res = await this.client.get(`/students/${studentId}/lifecycle`);
    return res.data.data;
  }

  async patchStudent(studentId: string, data: Record<string, unknown>) {
    const res = await this.client.patch(`/students/${studentId}`, data);
    return res.data.data;
  }

  async downloadStudentIdCard(studentId: string): Promise<Blob> {
    const res = await this.client.get(`/students/${studentId}/id-card/pdf`, { responseType: 'blob' });
    return res.data;
  }

  async downloadBulkIdCards(classSectionId: string): Promise<Blob> {
    const res = await this.client.get('/students/id-cards/bulk-pdf', {
      params: { classSectionId },
      responseType: 'blob',
    });
    return res.data;
  }

  async uploadStudentPhoto(studentId: string, file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('photo', file);
    const res = await this.client.post(`/students/${studentId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data;
  }

  // ─── Guardians ───
  async getMyWards() {
    const res = await this.client.get('/guardians/my-wards');
    return res.data.data;
  }

  async listGuardians(params?: Record<string, string>) {
    const res = await this.client.get('/guardians', { params });
    return res.data;
  }

  async getGuardian(guardianId: string) {
    const res = await this.client.get(`/guardians/${guardianId}`);
    return res.data.data;
  }

  async createGuardian(data: Record<string, unknown>) {
    const res = await this.client.post('/guardians', data);
    return res.data.data;
  }

  async updateGuardian(guardianId: string, data: Record<string, unknown>) {
    const res = await this.client.put(`/guardians/${guardianId}`, data);
    return res.data.data;
  }

  async linkGuardianStudent(data: Record<string, unknown>) {
    const res = await this.client.post('/guardians/link', data);
    return res.data.data;
  }

  async unlinkGuardianStudent(guardianId: string, data: Record<string, unknown>) {
    const res = await this.client.post(`/guardians/${guardianId}/unlink`, data);
    return res.data.data;
  }

  async uploadGuardianPhoto(guardianId: string, file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('photo', file);
    const res = await this.client.post(`/guardians/${guardianId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data;
  }

  // ─── Employees ───
  async listEmployees(params?: Record<string, string>) {
    const res = await this.client.get('/employees', { params });
    return res.data;
  }

  async getEmployee(employeeId: string) {
    const res = await this.client.get(`/employees/${employeeId}`);
    return res.data.data;
  }

  async createEmployee(data: Record<string, unknown>) {
    const res = await this.client.post('/employees', data);
    return res.data.data;
  }

  async updateEmployee(employeeId: string, data: Record<string, unknown>) {
    const res = await this.client.put(`/employees/${employeeId}`, data);
    return res.data.data;
  }

  async uploadEmployeePhoto(employeeId: string, file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('photo', file);
    const res = await this.client.post(`/employees/${employeeId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data;
  }

  async createTeacherProfile(data: Record<string, unknown>) {
    const res = await this.client.post('/employees/teacher-profile', data);
    return res.data.data;
  }

  async listTeachers(params?: Record<string, string>) {
    const res = await this.client.get('/employees/teachers', { params });
    return res.data;
  }

  async getTeacher(teacherId: string) {
    const res = await this.client.get(`/employees/teachers/${teacherId}`);
    return res.data.data;
  }

  async updateTeacherProfile(teacherId: string, data: Record<string, unknown>) {
    const res = await this.client.patch(`/employees/teachers/${teacherId}`, data);
    return res.data.data;
  }

  // ─── Academics ───
  async listAcademicYears(params?: Record<string, string>) {
    const res = await this.client.get('/academics/academic-years', { params });
    return res.data;
  }

  async createAcademicYear(data: Record<string, unknown>) {
    const res = await this.client.post('/academics/academic-years', data);
    return res.data.data;
  }

  async updateAcademicYear(id: string, data: Record<string, unknown>) {
    const res = await this.client.put(`/academics/academic-years/${id}`, data);
    return res.data.data;
  }

  async listClasses(params?: Record<string, string>) {
    const res = await this.client.get('/academics/classes', { params });
    return res.data;
  }

  async createClass(data: Record<string, unknown>) {
    const res = await this.client.post('/academics/classes', data);
    return res.data.data;
  }

  async updateClass(id: string, data: Record<string, unknown>) {
    const res = await this.client.put(`/academics/classes/${id}`, data);
    return res.data.data;
  }

  async listSections(params?: Record<string, string>) {
    const res = await this.client.get('/academics/sections', { params });
    return res.data;
  }

  async createSection(data: Record<string, unknown>) {
    const res = await this.client.post('/academics/sections', data);
    return res.data.data;
  }

  async listClassSections(params?: Record<string, string>) {
    const res = await this.client.get('/academics/class-sections', { params });
    return res.data;
  }

  async createClassSection(data: Record<string, unknown>) {
    const res = await this.client.post('/academics/class-sections', data);
    return res.data.data;
  }

  async listSubjects(params?: Record<string, string>) {
    const res = await this.client.get('/academics/subjects', { params });
    return res.data;
  }

  async createSubject(data: Record<string, unknown>) {
    const res = await this.client.post('/academics/subjects', data);
    return res.data.data;
  }

  async listSubjectAssignments(params?: Record<string, string>) {
    const res = await this.client.get('/academics/subject-assignments', { params });
    return res.data;
  }

  async createSubjectAssignment(data: Record<string, unknown>) {
    const res = await this.client.post('/academics/subject-assignments', data);
    return res.data.data;
  }

  async listTimetable(params?: Record<string, string>) {
    const res = await this.client.get('/academics/timetable', { params });
    return res.data;
  }

  async createTimetableEntry(data: Record<string, unknown>) {
    const res = await this.client.post('/academics/timetable', data);
    return res.data.data;
  }

  async deleteClass(classId: string) {
    const res = await this.client.delete(`/academics/classes/${classId}`);
    return res.data.data;
  }

  async deleteSection(sectionId: string) {
    const res = await this.client.delete(`/academics/sections/${sectionId}`);
    return res.data.data;
  }

  async deleteSubject(subjectId: string) {
    const res = await this.client.delete(`/academics/subjects/${subjectId}`);
    return res.data.data;
  }

  async deleteClassSection(classSectionId: string) {
    const res = await this.client.delete(`/academics/class-sections/${classSectionId}`);
    return res.data.data;
  }

  async deleteSubjectAssignment(assignmentId: string) {
    const res = await this.client.delete(`/academics/subject-assignments/${assignmentId}`);
    return res.data.data;
  }

  async deleteTimetableEntry(entryId: string) {
    const res = await this.client.delete(`/academics/timetable/${entryId}`);
    return res.data.data;
  }

  async listDepartments(params?: Record<string, string>) {
    const res = await this.client.get('/academics/departments', { params });
    return res.data;
  }

  async createDepartment(data: Record<string, unknown>) {
    const res = await this.client.post('/academics/departments', data);
    return res.data.data;
  }

  async listDesignations(params?: Record<string, string>) {
    const res = await this.client.get('/academics/designations', { params });
    return res.data;
  }

  async createDesignation(data: Record<string, unknown>) {
    const res = await this.client.post('/academics/designations', data);
    return res.data.data;
  }

  // ─── Attendance ───
  async getActiveEnrollments(classSectionId: string) {
    const res = await this.client.get('/attendance/students/enrollments', { params: { classSectionId } });
    return res.data;
  }

  async markStudentAttendance(data: Record<string, unknown>) {
    const res = await this.client.post('/attendance/students', data);
    return res.data.data;
  }

  async getStudentAttendance(params: Record<string, string>) {
    const res = await this.client.get('/attendance/students', { params });
    return res.data;
  }

  async markEmployeeAttendance(data: Record<string, unknown>) {
    const res = await this.client.post('/attendance/employees', data);
    return res.data.data;
  }

  async getEmployeeAttendance(params: Record<string, string>) {
    const res = await this.client.get('/attendance/employees', { params });
    return res.data;
  }

  async updateAttendanceRecord(recordId: string, data: Record<string, unknown>) {
    const res = await this.client.patch(`/attendance/${recordId}`, data);
    return res.data.data;
  }

  // ─── Assignments ───
  async listAssignments(params?: Record<string, string>) {
    const res = await this.client.get('/assignments', { params });
    return res.data;
  }

  async createAssignment(data: Record<string, unknown>) {
    const res = await this.client.post('/assignments', data);
    return res.data.data;
  }

  async getAssignment(assignmentId: string) {
    const res = await this.client.get(`/assignments/${assignmentId}`);
    return res.data.data;
  }

  async listSubmissions(assignmentId: string, params?: Record<string, string>) {
    const res = await this.client.get(`/assignments/${assignmentId}/submissions`, { params });
    return res.data;
  }

  async gradeSubmission(submissionId: string, data: Record<string, unknown>) {
    const res = await this.client.post(`/assignments/submissions/${submissionId}/grade`, data);
    return res.data.data;
  }

  async submitAssignment(assignmentId: string, data: Record<string, unknown>) {
    const res = await this.client.post(`/assignments/${assignmentId}/submissions`, data);
    return res.data.data;
  }

  async listMaterials(params?: Record<string, string>) {
    const res = await this.client.get('/assignments/materials', { params });
    return res.data;
  }

  async createMaterial(data: Record<string, unknown>) {
    const res = await this.client.post('/assignments/materials', data);
    return res.data.data;
  }

  // ─── Exams ───
  async listExamTypes(params?: Record<string, string>) {
    const res = await this.client.get('/exams/types', { params });
    return res.data;
  }

  async createExamType(data: Record<string, unknown>) {
    const res = await this.client.post('/exams/types', data);
    return res.data.data;
  }

  async listExams(params?: Record<string, string>) {
    const res = await this.client.get('/exams', { params });
    return res.data;
  }

  async getExam(examId: string) {
    const res = await this.client.get(`/exams/${examId}`);
    return res.data.data;
  }

  async createExam(data: Record<string, unknown>) {
    const res = await this.client.post('/exams', data);
    return res.data.data;
  }

  async addExamSubject(examId: string, data: Record<string, unknown>) {
    const res = await this.client.post(`/exams/${examId}/subjects`, data);
    return res.data.data;
  }

  async submitMarks(examSubjectId: string, data: Record<string, unknown>) {
    const res = await this.client.post(`/exams/subjects/${examSubjectId}/marks`, data);
    return res.data.data;
  }

  async getExamSubjects(examId: string, params?: Record<string, string>) {
    const res = await this.client.get(`/exams/${examId}/subjects`, { params });
    return res.data;
  }

  async getMarks(examSubjectId: string, params?: Record<string, string>) {
    const res = await this.client.get(`/exams/subjects/${examSubjectId}/marks`, { params });
    return res.data;
  }

  async generateReportCards(examId: string) {
    const res = await this.client.post(`/exams/${examId}/report-cards`);
    return res.data.data;
  }

  async publishExam(examId: string) {
    const res = await this.client.post(`/exams/${examId}/publish`);
    return res.data.data;
  }

  async publishReportCards(examId: string) {
    const res = await this.client.post(`/exams/${examId}/report-cards/publish`);
    return res.data.data;
  }

  async getStudentResults(studentId: string) {
    const res = await this.client.get(`/exams/student-results/${studentId}`);
    return res.data.data;
  }

  async getMyResults() {
    const res = await this.client.get('/exams/my-results');
    return res.data.data;
  }

  async getStudentResultsById(studentId: string) {
    const res = await this.client.get(`/exams/student-results/${studentId}`);
    return res.data;
  }

  async downloadReportCardPdf(reportCardId: string) {
    const res = await this.client.get(`/exams/report-cards/${reportCardId}/pdf`, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ReportCard-${reportCardId}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  async downloadReportCard(examId: string, studentId: string): Promise<Blob> {
    const res = await this.client.get(`/exams/${examId}/report-card/${studentId}/pdf`, { responseType: 'blob' });
    return res.data;
  }

  async downloadBulkReportCards(examId: string, classSectionId: string): Promise<Blob> {
    const res = await this.client.get(`/exams/${examId}/report-cards/bulk-pdf`, {
      params: { classSectionId },
      responseType: 'blob',
    });
    return res.data;
  }

  // ─── Fees ───
  async getMyFeesSummary() {
    const res = await this.client.get('/fees/my-summary');
    return res.data.data;
  }

  async listFeeCategories(params?: Record<string, string>) {
    const res = await this.client.get('/fees/categories', { params });
    return res.data;
  }

  async createFeeCategory(data: Record<string, unknown>) {
    const res = await this.client.post('/fees/categories', data);
    return res.data.data;
  }

  async listFeeStructures(params?: Record<string, string>) {
    const res = await this.client.get('/fees/structures', { params });
    return res.data;
  }

  async createFeeStructure(data: Record<string, unknown>) {
    const res = await this.client.post('/fees/structures', data);
    return res.data.data;
  }

  async getFeeStructure(structureId: string) {
    const res = await this.client.get(`/fees/structures/${structureId}`);
    return res.data.data;
  }

  async listInvoices(params?: Record<string, string>) {
    const res = await this.client.get('/fees/invoices', { params });
    return res.data;
  }

  async getInvoice(invoiceId: string) {
    const res = await this.client.get(`/fees/invoices/${invoiceId}`);
    return res.data.data;
  }

  async createInvoice(data: Record<string, unknown>) {
    const res = await this.client.post('/fees/invoices', data);
    return res.data.data;
  }

  async recordPayment(data: Record<string, unknown>) {
    const res = await this.client.post('/fees/payments', data);
    return res.data.data;
  }

  async assignFeeStructure(data: Record<string, unknown>) {
    const res = await this.client.post('/fees/assign', data);
    return res.data.data;
  }

  async requestRefund(paymentId: string, data: Record<string, unknown>) {
    const res = await this.client.post(`/fees/payments/${paymentId}/refund`, data);
    return res.data.data;
  }

  async approveRefund(refundId: string) {
    const res = await this.client.post(`/fees/refunds/${refundId}/approve`);
    return res.data.data;
  }

  async createPaymentSession(data: Record<string, unknown>) {
    const res = await this.client.post('/fees/payments/session', data);
    return res.data.data;
  }

  async getReceipt(receiptId: string) {
    const res = await this.client.get(`/fees/receipts/${receiptId}`);
    return res.data.data;
  }

  async downloadReceiptPdf(receiptId: string) {
    const res = await this.client.get(`/fees/receipts/${receiptId}/pdf`, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Receipt-${receiptId}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  async listReceiptsByInvoice(invoiceId: string) {
    const res = await this.client.get(`/fees/invoices/${invoiceId}/receipts`);
    return res.data.data;
  }

  // ─── Fee Enhancements ───

  async getFeeEnhancementStats() {
    const res = await this.client.get('/fees/enhancements/stats');
    return res.data.data;
  }

  async listInstallmentPlans(params?: Record<string, string>) {
    const res = await this.client.get('/fees/enhancements/installment-plans', { params });
    return res.data.data;
  }

  async createInstallmentPlan(data: Record<string, unknown>) {
    const res = await this.client.post('/fees/enhancements/installment-plans', data);
    return res.data.data;
  }

  async getInstallmentPlan(planId: string) {
    const res = await this.client.get(`/fees/enhancements/installment-plans/${planId}`);
    return res.data.data;
  }

  async deleteInstallmentPlan(planId: string) {
    const res = await this.client.delete(`/fees/enhancements/installment-plans/${planId}`);
    return res.data.data;
  }

  async listConcessions(params?: Record<string, string>) {
    const res = await this.client.get('/fees/enhancements/concessions', { params });
    return res.data.data;
  }

  async createConcession(data: Record<string, unknown>) {
    const res = await this.client.post('/fees/enhancements/concessions', data);
    return res.data.data;
  }

  async updateConcession(concessionId: string, data: Record<string, unknown>) {
    const res = await this.client.patch(`/fees/enhancements/concessions/${concessionId}`, data);
    return res.data.data;
  }

  async deleteConcession(concessionId: string) {
    const res = await this.client.delete(`/fees/enhancements/concessions/${concessionId}`);
    return res.data.data;
  }

  async assignStudentConcession(data: Record<string, unknown>) {
    const res = await this.client.post('/fees/enhancements/student-concessions', data);
    return res.data.data;
  }

  async listStudentConcessions(studentId: string) {
    const res = await this.client.get(`/fees/enhancements/student-concessions/${studentId}`);
    return res.data.data;
  }

  async revokeStudentConcession(studentConcessionId: string) {
    const res = await this.client.post(`/fees/enhancements/student-concessions/${studentConcessionId}/revoke`);
    return res.data.data;
  }

  async listLateFeeRules(params?: Record<string, string>) {
    const res = await this.client.get('/fees/enhancements/late-fee-rules', { params });
    return res.data.data;
  }

  async createLateFeeRule(data: Record<string, unknown>) {
    const res = await this.client.post('/fees/enhancements/late-fee-rules', data);
    return res.data.data;
  }

  async updateLateFeeRule(ruleId: string, data: Record<string, unknown>) {
    const res = await this.client.patch(`/fees/enhancements/late-fee-rules/${ruleId}`, data);
    return res.data.data;
  }

  async deleteLateFeeRule(ruleId: string) {
    const res = await this.client.delete(`/fees/enhancements/late-fee-rules/${ruleId}`);
    return res.data.data;
  }

  async calculateLateFee(invoiceId: string) {
    const res = await this.client.get(`/fees/enhancements/calculate-late-fee/${invoiceId}`);
    return res.data.data;
  }

  async getDefaulterReport(params?: Record<string, string>) {
    const res = await this.client.get('/fees/enhancements/defaulter-report', { params });
    return res.data;
  }

  async listFeeReminders(params?: Record<string, string>) {
    const res = await this.client.get('/fees/enhancements/reminders', { params });
    return res.data;
  }

  async createFeeReminder(data: Record<string, unknown>) {
    const res = await this.client.post('/fees/enhancements/reminders', data);
    return res.data.data;
  }

  // ─── HR ───
  async listLeaveTypes(params?: Record<string, string>) {
    const res = await this.client.get('/hr/leave-types', { params });
    return res.data;
  }

  async createLeaveType(data: Record<string, unknown>) {
    const res = await this.client.post('/hr/leave-types', data);
    return res.data.data;
  }

  async listLeaveRequests(params?: Record<string, string>) {
    const res = await this.client.get('/hr/leave-requests', { params });
    return res.data;
  }

  async createLeaveRequest(data: Record<string, unknown>) {
    const res = await this.client.post('/hr/leave-requests', data);
    return res.data.data;
  }

  async approveLeaveRequest(requestId: string) {
    const res = await this.client.post(`/hr/leave-requests/${requestId}/approve`);
    return res.data.data;
  }

  async rejectLeaveRequest(requestId: string, data: Record<string, unknown>) {
    const res = await this.client.post(`/hr/leave-requests/${requestId}/reject`, data);
    return res.data.data;
  }

  async listSalaryStructures(params?: Record<string, string>) {
    const res = await this.client.get('/hr/salary-structures', { params });
    return res.data;
  }

  async createSalaryStructure(data: Record<string, unknown>) {
    const res = await this.client.post('/hr/salary-structures', data);
    return res.data.data;
  }

  async listPayrollRuns(params?: Record<string, string>) {
    const res = await this.client.get('/hr/payroll-runs', { params });
    return res.data;
  }

  async createPayrollRun(data: Record<string, unknown>) {
    const res = await this.client.post('/hr/payroll-runs', data);
    return res.data.data;
  }

  async lockPayrollRun(runId: string) {
    const res = await this.client.post(`/hr/payroll-runs/${runId}/lock`);
    return res.data.data;
  }

  async payPayrollRun(runId: string) {
    const res = await this.client.post(`/hr/payroll-runs/${runId}/pay`);
    return res.data.data;
  }

  async getPayrollRun(runId: string) {
    const res = await this.client.get(`/hr/payroll-runs/${runId}`);
    return res.data.data;
  }

  async generatePayrollRunItems(runId: string) {
    const res = await this.client.post(`/hr/payroll-runs/${runId}/generate-items`);
    return res.data.data;
  }

  async listPayrollRunItems(runId: string) {
    const res = await this.client.get(`/hr/payroll-runs/${runId}/items`);
    return res.data;
  }

  async generatePayslips(runId: string) {
    const res = await this.client.post(`/hr/payroll-runs/${runId}/payslips`);
    return res.data.data;
  }

  async listPayslips(params?: Record<string, string>) {
    const res = await this.client.get('/hr/payslips', { params });
    return res.data;
  }

  async getPayslip(payslipId: string) {
    const res = await this.client.get(`/hr/payslips/${payslipId}`);
    return res.data.data;
  }

  async getSalaryStructure(structureId: string) {
    const res = await this.client.get(`/hr/salary-structures/${structureId}`);
    return res.data.data;
  }

  // ─── Inventory ───
  async listInventoryCategories(params?: Record<string, string>) {
    const res = await this.client.get('/inventory/categories', { params });
    return res.data;
  }

  async createInventoryCategory(data: Record<string, unknown>) {
    const res = await this.client.post('/inventory/categories', data);
    return res.data.data;
  }

  async listInventoryItems(params?: Record<string, string>) {
    const res = await this.client.get('/inventory/items', { params });
    return res.data;
  }

  async createInventoryItem(data: Record<string, unknown>) {
    const res = await this.client.post('/inventory/items', data);
    return res.data.data;
  }

  async updateInventoryItem(itemId: string, data: Record<string, unknown>) {
    const res = await this.client.patch(`/inventory/items/${itemId}`, data);
    return res.data.data;
  }

  async listVendors(params?: Record<string, string>) {
    const res = await this.client.get('/inventory/vendors', { params });
    return res.data;
  }

  async createVendor(data: Record<string, unknown>) {
    const res = await this.client.post('/inventory/vendors', data);
    return res.data.data;
  }

  async listPurchaseEntries(params?: Record<string, string>) {
    const res = await this.client.get('/inventory/purchases', { params });
    return res.data;
  }

  async createPurchaseEntry(data: Record<string, unknown>) {
    const res = await this.client.post('/inventory/purchases', data);
    return res.data.data;
  }

  async recordStockMovement(data: Record<string, unknown>) {
    const res = await this.client.post('/inventory/stock-movements', data);
    return res.data.data;
  }

  async listStockMovements(params?: Record<string, string>) {
    const res = await this.client.get('/inventory/movements', { params });
    return res.data;
  }

  async adjustStock(data: Record<string, unknown>) {
    const res = await this.client.post('/inventory/adjust', data);
    return res.data.data;
  }

  async issueStock(data: Record<string, unknown>) {
    const res = await this.client.post('/inventory/issue', data);
    return res.data.data;
  }

  async returnStock(data: Record<string, unknown>) {
    const res = await this.client.post('/inventory/return', data);
    return res.data.data;
  }

  // ─── Communication ───
  async listNotifications(params?: Record<string, string>) {
    const res = await this.client.get('/communication/notifications', { params });
    return res.data;
  }

  async markNotificationRead(notificationId: string) {
    return this.client.post(`/communication/notifications/${notificationId}/read`);
  }

  async markAllNotificationsRead() {
    return this.client.post('/communication/notifications/read-all');
  }

  async listMessages(params?: Record<string, string>) {
    const res = await this.client.get('/communication/messages', { params });
    return res.data;
  }

  async sendMessage(data: Record<string, unknown>) {
    const res = await this.client.post('/communication/messages', data);
    return res.data.data;
  }

  async listAnnouncements(params?: Record<string, string>) {
    const res = await this.client.get('/communication/announcements', { params });
    return res.data;
  }

  async createAnnouncement(data: Record<string, unknown>) {
    const res = await this.client.post('/communication/announcements', data);
    return res.data.data;
  }

  // ─── Audit ───
  async listAuditEvents(params?: Record<string, string>) {
    const res = await this.client.get('/audit', { params });
    return res.data;
  }

  // ─── Reports ───
  async getDashboard() {
    const res = await this.client.get('/dashboard');
    return res.data.data;
  }

  async getMyClassSections() {
    const res = await this.client.get('/attendance/my-class-sections');
    return res.data.data;
  }

  async getAttendanceReport(params: Record<string, string>) {
    const res = await this.client.get('/reports/attendance', { params });
    return res.data.data;
  }

  async getFeeCollectionReport(params?: Record<string, string>) {
    const res = await this.client.get('/reports/fee-collection', { params });
    return res.data.data;
  }

  async getStudentStrengthReport(params: Record<string, string>) {
    const res = await this.client.get('/reports/student-strength', { params });
    return res.data.data;
  }

  async getEmployeeSummaryReport(params?: Record<string, string>) {
    const res = await this.client.get('/reports/employee-summary', { params });
    return res.data.data;
  }

  // ─── Library ───
  library = {
    getStats: async () => {
      const res = await this.client.get('/library/stats');
      return res.data;
    },
    listCategories: async () => {
      const res = await this.client.get('/library/categories');
      return res.data;
    },
    createCategory: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/library/categories', data);
      return res.data;
    },
    listBooks: async (search?: string, categoryId?: string) => {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (categoryId) params.categoryId = categoryId;
      const res = await this.client.get('/library/books', { params });
      return res.data;
    },
    getBook: async (bookId: string) => {
      const res = await this.client.get(`/library/books/${bookId}`);
      return res.data;
    },
    createBook: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/library/books', data);
      return res.data;
    },
    updateBook: async (bookId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/library/books/${bookId}`, data);
      return res.data;
    },
    deleteBook: async (bookId: string) => {
      const res = await this.client.delete(`/library/books/${bookId}`);
      return res.data;
    },
    listIssues: async (params?: Record<string, string>) => {
      const res = await this.client.get('/library/issues', { params });
      return res.data;
    },
    getIssue: async (issueId: string) => {
      const res = await this.client.get(`/library/issues/${issueId}`);
      return res.data;
    },
    issueBook: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/library/issues', data);
      return res.data;
    },
    returnBook: async (issueId: string, data: Record<string, unknown>) => {
      const res = await this.client.post(`/library/issues/${issueId}/return`, data);
      return res.data;
    },
    getMyIssues: async () => {
      const res = await this.client.get('/library/my-issues');
      return res.data;
    },
  };

  // ─── Transport ───
  transport = {
    listVehicles: async () => {
      const res = await this.client.get('/transport/vehicles');
      return res.data;
    },
    getVehicle: async (vehicleId: string) => {
      const res = await this.client.get(`/transport/vehicles/${vehicleId}`);
      return res.data;
    },
    createVehicle: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/transport/vehicles', data);
      return res.data;
    },
    updateVehicle: async (vehicleId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/transport/vehicles/${vehicleId}`, data);
      return res.data;
    },
    listRoutes: async () => {
      const res = await this.client.get('/transport/routes');
      return res.data;
    },
    getRoute: async (routeId: string) => {
      const res = await this.client.get(`/transport/routes/${routeId}`);
      return res.data;
    },
    createRoute: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/transport/routes', data);
      return res.data;
    },
    updateRoute: async (routeId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/transport/routes/${routeId}`, data);
      return res.data;
    },
    addStop: async (routeId: string, data: Record<string, unknown>) => {
      const res = await this.client.post(`/transport/routes/${routeId}/stops`, data);
      return res.data;
    },
    removeStop: async (stopId: string) => {
      const res = await this.client.delete(`/transport/stops/${stopId}`);
      return res.data;
    },
    listAssignments: async (routeId?: string) => {
      const params: Record<string, string> = {};
      if (routeId) params.routeId = routeId;
      const res = await this.client.get('/transport/assignments', { params });
      return res.data;
    },
    assignStudent: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/transport/assignments', data);
      return res.data;
    },
    getMyTransport: async () => {
      const res = await this.client.get('/transport/my-transport');
      return res.data;
    },
  };

  // ─── Front Office ───
  frontOffice = {
    getStats: async () => {
      const res = await this.client.get('/front-office/stats');
      return res.data;
    },
    listVisitors: async (date?: string) => {
      const params: Record<string, string> = {};
      if (date) params.date = date;
      const res = await this.client.get('/front-office/visitors', { params });
      return res.data;
    },
    getVisitor: async (visitorId: string) => {
      const res = await this.client.get(`/front-office/visitors/${visitorId}`);
      return res.data;
    },
    createVisitor: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/front-office/visitors', data);
      return res.data;
    },
    checkOutVisitor: async (visitorId: string) => {
      const res = await this.client.post(`/front-office/visitors/${visitorId}/checkout`);
      return res.data;
    },
    listEnquiries: async (status?: string) => {
      const params: Record<string, string> = {};
      if (status) params.status = status;
      const res = await this.client.get('/front-office/enquiries', { params });
      return res.data;
    },
    getEnquiry: async (enquiryId: string) => {
      const res = await this.client.get(`/front-office/enquiries/${enquiryId}`);
      return res.data;
    },
    createEnquiry: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/front-office/enquiries', data);
      return res.data;
    },
    updateEnquiry: async (enquiryId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/front-office/enquiries/${enquiryId}`, data);
      return res.data;
    },
    listPostalRecords: async (type?: string) => {
      const params: Record<string, string> = {};
      if (type) params.type = type;
      const res = await this.client.get('/front-office/postal', { params });
      return res.data;
    },
    getPostalRecord: async (postalId: string) => {
      const res = await this.client.get(`/front-office/postal/${postalId}`);
      return res.data;
    },
    createPostalRecord: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/front-office/postal', data);
      return res.data;
    },
  };

  // ─── Hostel & Mess ───
  hostel = {
    getStats: async () => {
      const res = await this.client.get('/hostel/stats');
      return res.data;
    },
    listBuildings: async () => {
      const res = await this.client.get('/hostel/buildings');
      return res.data;
    },
    getBuilding: async (buildingId: string) => {
      const res = await this.client.get(`/hostel/buildings/${buildingId}`);
      return res.data;
    },
    createBuilding: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/hostel/buildings', data);
      return res.data;
    },
    updateBuilding: async (buildingId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/hostel/buildings/${buildingId}`, data);
      return res.data;
    },
    listRooms: async (buildingId?: string) => {
      const params: Record<string, string> = {};
      if (buildingId) params.buildingId = buildingId;
      const res = await this.client.get('/hostel/rooms', { params });
      return res.data;
    },
    getRoom: async (roomId: string) => {
      const res = await this.client.get(`/hostel/rooms/${roomId}`);
      return res.data;
    },
    createRoom: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/hostel/rooms', data);
      return res.data;
    },
    updateRoom: async (roomId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/hostel/rooms/${roomId}`, data);
      return res.data;
    },
    listAllocations: async (buildingId?: string, status?: string) => {
      const params: Record<string, string> = {};
      if (buildingId) params.buildingId = buildingId;
      if (status) params.status = status;
      const res = await this.client.get('/hostel/allocations', { params });
      return res.data;
    },
    allocateStudent: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/hostel/allocations', data);
      return res.data;
    },
    vacateAllocation: async (allocationId: string) => {
      const res = await this.client.post(`/hostel/allocations/${allocationId}/vacate`);
      return res.data;
    },
    listMessMenus: async () => {
      const res = await this.client.get('/hostel/mess-menu');
      return res.data;
    },
    createMessMenu: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/hostel/mess-menu', data);
      return res.data;
    },
    updateMessMenu: async (menuId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/hostel/mess-menu/${menuId}`, data);
      return res.data;
    },
    getMyAllocation: async () => {
      const res = await this.client.get('/hostel/my-allocation');
      return res.data;
    },
  };

  // ─── Infirmary ───
  infirmary = {
    getStats: async () => {
      const res = await this.client.get('/infirmary/stats');
      return res.data;
    },
    listVisits: async (params?: Record<string, string>) => {
      const res = await this.client.get('/infirmary/visits', { params });
      return res.data;
    },
    getVisit: async (visitId: string) => {
      const res = await this.client.get(`/infirmary/visits/${visitId}`);
      return res.data;
    },
    createVisit: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/infirmary/visits', data);
      return res.data;
    },
    updateVisit: async (visitId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/infirmary/visits/${visitId}`, data);
      return res.data;
    },
    listMedicines: async () => {
      const res = await this.client.get('/infirmary/medicines');
      return res.data;
    },
    getMedicine: async (medicineId: string) => {
      const res = await this.client.get(`/infirmary/medicines/${medicineId}`);
      return res.data;
    },
    createMedicine: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/infirmary/medicines', data);
      return res.data;
    },
    updateMedicine: async (medicineId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/infirmary/medicines/${medicineId}`, data);
      return res.data;
    },
  };

  // ─── Health & Welfare ───
  healthWelfare = {
    getStats: async () => {
      const res = await this.client.get('/infirmary/health/stats');
      return res.data;
    },
    // Vaccinations
    listVaccinations: async (params?: Record<string, string>) => {
      const res = await this.client.get('/infirmary/health/vaccinations', { params });
      return res.data;
    },
    createVaccination: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/infirmary/health/vaccinations', data);
      return res.data;
    },
    getVaccination: async (id: string) => {
      const res = await this.client.get(`/infirmary/health/vaccinations/${id}`);
      return res.data;
    },
    updateVaccination: async (id: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/infirmary/health/vaccinations/${id}`, data);
      return res.data;
    },
    getStudentVaccinations: async (studentId: string) => {
      const res = await this.client.get(`/infirmary/health/vaccinations/student/${studentId}`);
      return res.data;
    },
    getOverdueVaccinations: async () => {
      const res = await this.client.get('/infirmary/health/vaccinations/overdue');
      return res.data;
    },
    // Health Checkups
    listCheckups: async (params?: Record<string, string>) => {
      const res = await this.client.get('/infirmary/health/checkups', { params });
      return res.data;
    },
    createCheckup: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/infirmary/health/checkups', data);
      return res.data;
    },
    getCheckup: async (id: string) => {
      const res = await this.client.get(`/infirmary/health/checkups/${id}`);
      return res.data;
    },
    updateCheckup: async (id: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/infirmary/health/checkups/${id}`, data);
      return res.data;
    },
    getStudentCheckups: async (studentId: string) => {
      const res = await this.client.get(`/infirmary/health/checkups/student/${studentId}`);
      return res.data;
    },
    // Counseling
    listSessions: async (params?: Record<string, string>) => {
      const res = await this.client.get('/infirmary/health/counseling', { params });
      return res.data;
    },
    createSession: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/infirmary/health/counseling', data);
      return res.data;
    },
    getSession: async (id: string) => {
      const res = await this.client.get(`/infirmary/health/counseling/${id}`);
      return res.data;
    },
    updateSession: async (id: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/infirmary/health/counseling/${id}`, data);
      return res.data;
    },
    getStudentSessions: async (studentId: string) => {
      const res = await this.client.get(`/infirmary/health/counseling/student/${studentId}`);
      return res.data;
    },
  };

  // ─── Events & Calendar ───
  events = {
    getCalendar: async (month?: string, year?: string) => {
      const params: Record<string, string> = {};
      if (month) params.month = month;
      if (year) params.year = year;
      const res = await this.client.get('/events/calendar', { params });
      return res.data;
    },
    getUpcoming: async (limit?: number) => {
      const params: Record<string, string> = {};
      if (limit) params.limit = String(limit);
      const res = await this.client.get('/events/upcoming', { params });
      return res.data;
    },
    listEvents: async (params?: Record<string, string>) => {
      const res = await this.client.get('/events', { params });
      return res.data;
    },
    getEvent: async (eventId: string) => {
      const res = await this.client.get(`/events/${eventId}`);
      return res.data;
    },
    createEvent: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/events', data);
      return res.data;
    },
    updateEvent: async (eventId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/events/${eventId}`, data);
      return res.data;
    },
    deleteEvent: async (eventId: string) => {
      const res = await this.client.delete(`/events/${eventId}`);
      return res.data;
    },
  };

  // ─── Admissions ───
  admissions = {
    getStats: async () => {
      const res = await this.client.get('/admissions/stats');
      return res.data;
    },
    listCycles: async () => {
      const res = await this.client.get('/admissions/cycles');
      return res.data;
    },
    createCycle: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/admissions/cycles', data);
      return res.data;
    },
    listApplications: async (params?: Record<string, string>) => {
      const res = await this.client.get('/admissions/applications', { params });
      return res.data;
    },
    getApplication: async (id: string) => {
      const res = await this.client.get(`/admissions/applications/${id}`);
      return res.data;
    },
    createApplication: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/admissions/applications', data);
      return res.data;
    },
    updateApplication: async (id: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/admissions/applications/${id}`, data);
      return res.data;
    },
    updateStage: async (id: string, stage: string) => {
      const res = await this.client.post(`/admissions/applications/${id}/stage`, { stage });
      return res.data;
    },
    addDocument: async (applicationId: string, data: Record<string, unknown>) => {
      const res = await this.client.post(`/admissions/applications/${applicationId}/documents`, data);
      return res.data;
    },
    listDocuments: async (applicationId: string) => {
      const res = await this.client.get(`/admissions/applications/${applicationId}/documents`);
      return res.data;
    },
    listSeats: async (cycleId?: string) => {
      const params: Record<string, string> = {};
      if (cycleId) params.cycleId = cycleId;
      const res = await this.client.get('/admissions/seats', { params });
      return res.data;
    },
    upsertSeats: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/admissions/seats', data);
      return res.data;
    },
    convertToStudent: async (applicationId: string) => {
      const res = await this.client.post(`/admissions/applications/${applicationId}/convert-to-student`);
      return res.data;
    },
  };

  // ─── Certificates ───
  certificates = {
    getStats: async () => {
      const res = await this.client.get('/certificates/stats');
      return res.data;
    },
    listTemplates: async () => {
      const res = await this.client.get('/certificates/templates');
      return res.data;
    },
    createTemplate: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/certificates/templates', data);
      return res.data;
    },
    getTemplate: async (id: string) => {
      const res = await this.client.get(`/certificates/templates/${id}`);
      return res.data;
    },
    updateTemplate: async (id: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/certificates/templates/${id}`, data);
      return res.data;
    },
    deleteTemplate: async (id: string) => {
      const res = await this.client.delete(`/certificates/templates/${id}`);
      return res.data;
    },
    listIssued: async (params?: Record<string, string>) => {
      const res = await this.client.get('/certificates/issued', { params });
      return res.data;
    },
    issueCertificate: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/certificates/issued', data);
      return res.data;
    },
    getCertificate: async (id: string) => {
      const res = await this.client.get(`/certificates/issued/${id}`);
      return res.data;
    },
    revokeCertificate: async (id: string, reason: string) => {
      const res = await this.client.post(`/certificates/issued/${id}/revoke`, { reason });
      return res.data;
    },
  };

  // ─── Discipline ───
  discipline = {
    getStats: async () => {
      const res = await this.client.get('/discipline/stats');
      return res.data;
    },
    listIncidents: async (params?: Record<string, string>) => {
      const res = await this.client.get('/discipline/incidents', { params });
      return res.data;
    },
    createIncident: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/discipline/incidents', data);
      return res.data;
    },
    getIncident: async (id: string) => {
      const res = await this.client.get(`/discipline/incidents/${id}`);
      return res.data;
    },
    updateIncident: async (id: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/discipline/incidents/${id}`, data);
      return res.data;
    },
    updateStatus: async (id: string, status: string, resolutionNotes?: string) => {
      const res = await this.client.post(`/discipline/incidents/${id}/status`, { status, resolutionNotes });
      return res.data;
    },
    listActions: async (incidentId: string) => {
      const res = await this.client.get(`/discipline/incidents/${incidentId}/actions`);
      return res.data;
    },
    createAction: async (incidentId: string, data: Record<string, unknown>) => {
      const res = await this.client.post(`/discipline/incidents/${incidentId}/actions`, data);
      return res.data;
    },
    updateAction: async (actionId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/discipline/actions/${actionId}`, data);
      return res.data;
    },
    getStudentHistory: async (studentId: string) => {
      const res = await this.client.get(`/discipline/student/${studentId}`);
      return res.data;
    },
  };

  // ─── Grading ───
  grading = {
    listScales: async () => {
      const res = await this.client.get('/exams/grading/scales');
      return res.data;
    },
    createScale: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/exams/grading/scales', data);
      return res.data;
    },
    getScale: async (id: string) => {
      const res = await this.client.get(`/exams/grading/scales/${id}`);
      return res.data;
    },
    updateScale: async (id: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/exams/grading/scales/${id}`, data);
      return res.data;
    },
    deleteScale: async (id: string) => {
      const res = await this.client.delete(`/exams/grading/scales/${id}`);
      return res.data;
    },
    getEntries: async (scaleId: string) => {
      const res = await this.client.get(`/exams/grading/scales/${scaleId}/entries`);
      return res.data;
    },
    setEntries: async (scaleId: string, entries: any[]) => {
      const res = await this.client.put(`/exams/grading/scales/${scaleId}/entries`, { entries });
      return res.data;
    },
    listCoScholastic: async () => {
      const res = await this.client.get('/exams/grading/co-scholastic');
      return res.data;
    },
    createCoScholastic: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/exams/grading/co-scholastic', data);
      return res.data;
    },
    deleteCoScholastic: async (id: string) => {
      const res = await this.client.delete(`/exams/grading/co-scholastic/${id}`);
      return res.data;
    },
    submitCoScholasticMarks: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/exams/grading/co-scholastic-marks', data);
      return res.data;
    },
    getCoScholasticMarks: async (params?: Record<string, string>) => {
      const res = await this.client.get('/exams/grading/co-scholastic-marks', { params });
      return res.data;
    },
    getGPA: async (studentId: string, examId: string) => {
      const res = await this.client.get(`/exams/grading/gpa/${studentId}/${examId}`);
      return res.data;
    },
    getRankList: async (examId: string) => {
      const res = await this.client.get(`/exams/grading/rank-list/${examId}`);
      return res.data;
    },
    getAnalytics: async (examId: string) => {
      const res = await this.client.get(`/exams/grading/analytics/${examId}`);
      return res.data;
    },
  };

  // ─── Substitutions ───
  substitutions = {
    getStats: async () => {
      const res = await this.client.get('/academics/substitutions/stats');
      return res.data;
    },
    listAbsences: async (params?: Record<string, string>) => {
      const res = await this.client.get('/academics/substitutions/absences', { params });
      return res.data;
    },
    reportAbsence: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/academics/substitutions/absences', data);
      return res.data;
    },
    getAbsence: async (id: string) => {
      const res = await this.client.get(`/academics/substitutions/absences/${id}`);
      return res.data;
    },
    getAvailableTeachers: async (date: string, periodNumber: number) => {
      const res = await this.client.get('/academics/substitutions/available-teachers', {
        params: { date, periodNumber: String(periodNumber) },
      });
      return res.data;
    },
    listSubstitutions: async (params?: Record<string, string>) => {
      const res = await this.client.get('/academics/substitutions', { params });
      return res.data;
    },
    createSubstitution: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/academics/substitutions', data);
      return res.data;
    },
    updateStatus: async (id: string, status: string) => {
      const res = await this.client.patch(`/academics/substitutions/${id}/status`, { status });
      return res.data;
    },
    getWorkloadReport: async (params?: Record<string, string>) => {
      const res = await this.client.get('/academics/substitutions/workload-report', { params });
      return res.data;
    },
  };

  // ─── Accounting ───
  accounting = {
    getStats: async () => {
      const res = await this.client.get('/accounting/stats');
      return res.data;
    },
    listAccountHeads: async () => {
      const res = await this.client.get('/accounting/account-heads');
      return res.data;
    },
    createAccountHead: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/accounting/account-heads', data);
      return res.data;
    },
    getAccountHead: async (id: string) => {
      const res = await this.client.get(`/accounting/account-heads/${id}`);
      return res.data;
    },
    updateAccountHead: async (id: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/accounting/account-heads/${id}`, data);
      return res.data;
    },
    listVouchers: async (params?: Record<string, string>) => {
      const res = await this.client.get('/accounting/vouchers', { params });
      return res.data;
    },
    createVoucher: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/accounting/vouchers', data);
      return res.data;
    },
    getVoucher: async (id: string) => {
      const res = await this.client.get(`/accounting/vouchers/${id}`);
      return res.data;
    },
    approveVoucher: async (id: string) => {
      const res = await this.client.post(`/accounting/vouchers/${id}/approve`);
      return res.data;
    },
    cancelVoucher: async (id: string) => {
      const res = await this.client.post(`/accounting/vouchers/${id}/cancel`);
      return res.data;
    },
    listBudgets: async () => {
      const res = await this.client.get('/accounting/budgets');
      return res.data;
    },
    createBudget: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/accounting/budgets', data);
      return res.data;
    },
    updateBudget: async (id: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/accounting/budgets/${id}`, data);
      return res.data;
    },
    getDayBook: async (date: string) => {
      const res = await this.client.get('/accounting/reports/day-book', { params: { date } });
      return res.data;
    },
    getCashBook: async (dateFrom: string, dateTo: string) => {
      const res = await this.client.get('/accounting/reports/cash-book', { params: { dateFrom, dateTo } });
      return res.data;
    },
    getTrialBalance: async (dateFrom: string, dateTo: string) => {
      const res = await this.client.get('/accounting/reports/trial-balance', { params: { dateFrom, dateTo } });
      return res.data;
    },
    getIncomeExpense: async (dateFrom: string, dateTo: string) => {
      const res = await this.client.get('/accounting/reports/income-expense', { params: { dateFrom, dateTo } });
      return res.data;
    },
  };

  // ─── Enhanced Reports ───
  enhancedReports = {
    getClassPerformance: async (params?: Record<string, string>) => {
      const res = await this.client.get('/reports/enhanced/class-performance', { params });
      return res.data.data;
    },
    getTeacherWorkload: async () => {
      const res = await this.client.get('/reports/enhanced/teacher-workload');
      return res.data.data;
    },
    getFeeDefaulters: async (params?: Record<string, string>) => {
      const res = await this.client.get('/reports/enhanced/fee-defaulters', { params });
      return res.data.data;
    },
    getTransportUtilization: async () => {
      const res = await this.client.get('/reports/enhanced/transport-utilization');
      return res.data.data;
    },
    getLibraryUsage: async () => {
      const res = await this.client.get('/reports/enhanced/library-usage');
      return res.data.data;
    },
    getHostelOccupancy: async () => {
      const res = await this.client.get('/reports/enhanced/hostel-occupancy');
      return res.data.data;
    },
    getAttendanceSummary: async (params?: Record<string, string>) => {
      const res = await this.client.get('/reports/enhanced/attendance-summary', { params });
      return res.data.data;
    },
    getExamComparison: async (examId1: string, examId2: string) => {
      const res = await this.client.get('/reports/enhanced/exam-comparison', {
        params: { examId1, examId2 },
      });
      return res.data.data;
    },
  };

  // ─── Grievances ───
  grievances = {
    getStats: async () => {
      const res = await this.client.get('/grievances/stats');
      return res.data;
    },
    listComplaints: async (params?: Record<string, string>) => {
      const res = await this.client.get('/grievances', { params });
      return res.data;
    },
    createComplaint: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/grievances', data);
      return res.data;
    },
    getComplaint: async (id: string) => {
      const res = await this.client.get(`/grievances/${id}`);
      return res.data;
    },
    updateComplaint: async (id: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/grievances/${id}`, data);
      return res.data;
    },
    assignComplaint: async (id: string, assignedTo: string) => {
      const res = await this.client.post(`/grievances/${id}/assign`, { assignedTo });
      return res.data;
    },
    updateStatus: async (id: string, status: string, resolutionNotes?: string) => {
      const res = await this.client.post(`/grievances/${id}/status`, { status, resolutionNotes });
      return res.data;
    },
    listResponses: async (complaintId: string) => {
      const res = await this.client.get(`/grievances/${complaintId}/responses`);
      return res.data;
    },
    addResponse: async (complaintId: string, data: Record<string, unknown>) => {
      const res = await this.client.post(`/grievances/${complaintId}/responses`, data);
      return res.data;
    },
  };

  // ─── Alerts / Early Warning ───
  alerts = {
    getStats: async () => {
      const res = await this.client.get('/alerts/stats');
      return res.data;
    },
    listRules: async () => {
      const res = await this.client.get('/alerts/rules');
      return res.data;
    },
    createRule: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/alerts/rules', data);
      return res.data;
    },
    updateRule: async (ruleId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/alerts/rules/${ruleId}`, data);
      return res.data;
    },
    deleteRule: async (ruleId: string) => {
      const res = await this.client.delete(`/alerts/rules/${ruleId}`);
      return res.data;
    },
    listInstances: async (params?: Record<string, string>) => {
      const res = await this.client.get('/alerts/instances', { params });
      return res.data;
    },
    markRead: async (alertId: string) => {
      const res = await this.client.post(`/alerts/instances/${alertId}/read`);
      return res.data;
    },
    markResolved: async (alertId: string) => {
      const res = await this.client.post(`/alerts/instances/${alertId}/resolve`);
      return res.data;
    },
    markAllRead: async () => {
      const res = await this.client.post('/alerts/instances/read-all');
      return res.data;
    },
    runChecks: async () => {
      const res = await this.client.post('/alerts/run-checks');
      return res.data;
    },
  };

  // ─── Dashboard Advanced ───
  dashboardAdvanced = {
    getKPIs: async () => {
      const res = await this.client.get('/dashboard/advanced/kpis');
      return res.data;
    },
    getAttendanceTrend: async (months?: number) => {
      const res = await this.client.get('/dashboard/advanced/attendance-trend', {
        params: months ? { months: String(months) } : undefined,
      });
      return res.data;
    },
    getFeeTrend: async (months?: number) => {
      const res = await this.client.get('/dashboard/advanced/fee-trend', {
        params: months ? { months: String(months) } : undefined,
      });
      return res.data;
    },
    getStudentStrength: async () => {
      const res = await this.client.get('/dashboard/advanced/student-strength');
      return res.data;
    },
    getRecentActivity: async (limit?: number) => {
      const res = await this.client.get('/dashboard/advanced/recent-activity', {
        params: limit ? { limit: String(limit) } : undefined,
      });
      return res.data;
    },
    getAlerts: async () => {
      const res = await this.client.get('/dashboard/advanced/alerts');
      return res.data;
    },
  };

  // ─── HR Enhancements ───
  hrEnhancements = {
    getStats: async () => {
      const res = await this.client.get('/hr/enhancements/stats');
      return res.data;
    },
    // Appraisals
    listAppraisals: async (params?: Record<string, string>) => {
      const res = await this.client.get('/hr/enhancements/appraisals', { params });
      return res.data;
    },
    createAppraisal: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/hr/enhancements/appraisals', data);
      return res.data;
    },
    getAppraisal: async (id: string) => {
      const res = await this.client.get(`/hr/enhancements/appraisals/${id}`);
      return res.data;
    },
    updateAppraisal: async (id: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/hr/enhancements/appraisals/${id}`, data);
      return res.data;
    },
    submitAppraisal: async (id: string) => {
      const res = await this.client.post(`/hr/enhancements/appraisals/${id}/submit`);
      return res.data;
    },
    reviewAppraisal: async (id: string, data: Record<string, unknown>) => {
      const res = await this.client.post(`/hr/enhancements/appraisals/${id}/review`, data);
      return res.data;
    },
    acknowledgeAppraisal: async (id: string) => {
      const res = await this.client.post(`/hr/enhancements/appraisals/${id}/acknowledge`);
      return res.data;
    },
    // Training
    listTraining: async (params?: Record<string, string>) => {
      const res = await this.client.get('/hr/enhancements/training', { params });
      return res.data;
    },
    createTraining: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/hr/enhancements/training', data);
      return res.data;
    },
    getTraining: async (id: string) => {
      const res = await this.client.get(`/hr/enhancements/training/${id}`);
      return res.data;
    },
    updateTraining: async (id: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/hr/enhancements/training/${id}`, data);
      return res.data;
    },
    deleteTraining: async (id: string) => {
      const res = await this.client.delete(`/hr/enhancements/training/${id}`);
      return res.data;
    },
    // Claims
    listClaims: async (params?: Record<string, string>) => {
      const res = await this.client.get('/hr/enhancements/claims', { params });
      return res.data;
    },
    createClaim: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/hr/enhancements/claims', data);
      return res.data;
    },
    getClaim: async (id: string) => {
      const res = await this.client.get(`/hr/enhancements/claims/${id}`);
      return res.data;
    },
    approveClaim: async (id: string) => {
      const res = await this.client.post(`/hr/enhancements/claims/${id}/approve`);
      return res.data;
    },
    rejectClaim: async (id: string, reason: string) => {
      const res = await this.client.post(`/hr/enhancements/claims/${id}/reject`, { reason });
      return res.data;
    },
    markClaimPaid: async (id: string) => {
      const res = await this.client.post(`/hr/enhancements/claims/${id}/paid`);
      return res.data;
    },
  };

  // ─── Assets ───
  assets = {
    getStats: async () => {
      const res = await this.client.get('/inventory/assets/stats');
      return res.data;
    },
    list: async (params?: Record<string, string>) => {
      const res = await this.client.get('/inventory/assets', { params });
      return res.data;
    },
    get: async (assetId: string) => {
      const res = await this.client.get(`/inventory/assets/${assetId}`);
      return res.data;
    },
    create: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/inventory/assets', data);
      return res.data;
    },
    update: async (assetId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/inventory/assets/${assetId}`, data);
      return res.data;
    },
    dispose: async (assetId: string, reason: string) => {
      const res = await this.client.post(`/inventory/assets/${assetId}/dispose`, { reason });
      return res.data;
    },
    getDepreciation: async (assetId: string) => {
      const res = await this.client.get(`/inventory/assets/${assetId}/depreciation`);
      return res.data;
    },
    listMaintenance: async (assetId: string) => {
      const res = await this.client.get(`/inventory/assets/${assetId}/maintenance`);
      return res.data;
    },
    createMaintenance: async (assetId: string, data: Record<string, unknown>) => {
      const res = await this.client.post(`/inventory/assets/${assetId}/maintenance`, data);
      return res.data;
    },
    updateMaintenance: async (logId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/inventory/assets/maintenance/${logId}`, data);
      return res.data;
    },
    getUpcomingMaintenance: async () => {
      const res = await this.client.get('/inventory/assets/maintenance/upcoming');
      return res.data;
    },
  };

  // ─── Lesson Plans ───
  lessonPlans = {
    getStats: async () => {
      const res = await this.client.get('/lesson-plans/plans/stats');
      return res.data;
    },
    list: async (params?: Record<string, string>) => {
      const res = await this.client.get('/lesson-plans/plans', { params });
      return res.data;
    },
    get: async (planId: string) => {
      const res = await this.client.get(`/lesson-plans/plans/${planId}`);
      return res.data;
    },
    create: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/lesson-plans/plans', data);
      return res.data;
    },
    update: async (planId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/lesson-plans/plans/${planId}`, data);
      return res.data;
    },
    delete: async (planId: string) => {
      const res = await this.client.delete(`/lesson-plans/plans/${planId}`);
      return res.data;
    },
    submit: async (planId: string) => {
      const res = await this.client.post(`/lesson-plans/plans/${planId}/submit`);
      return res.data;
    },
    approve: async (planId: string) => {
      const res = await this.client.post(`/lesson-plans/plans/${planId}/approve`);
      return res.data;
    },
    reject: async (planId: string, remarks: string) => {
      const res = await this.client.post(`/lesson-plans/plans/${planId}/reject`, { remarks });
      return res.data;
    },
  };

  // ─── Quizzes ───
  quizzes = {
    getStats: async () => {
      const res = await this.client.get('/lesson-plans/quizzes/stats');
      return res.data;
    },
    list: async (params?: Record<string, string>) => {
      const res = await this.client.get('/lesson-plans/quizzes', { params });
      return res.data;
    },
    get: async (quizId: string) => {
      const res = await this.client.get(`/lesson-plans/quizzes/${quizId}`);
      return res.data;
    },
    create: async (data: Record<string, unknown>) => {
      const res = await this.client.post('/lesson-plans/quizzes', data);
      return res.data;
    },
    update: async (quizId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/lesson-plans/quizzes/${quizId}`, data);
      return res.data;
    },
    delete: async (quizId: string) => {
      const res = await this.client.delete(`/lesson-plans/quizzes/${quizId}`);
      return res.data;
    },
    publish: async (quizId: string) => {
      const res = await this.client.post(`/lesson-plans/quizzes/${quizId}/publish`);
      return res.data;
    },
    getQuestions: async (quizId: string) => {
      const res = await this.client.get(`/lesson-plans/quizzes/${quizId}/questions`);
      return res.data;
    },
    addQuestion: async (quizId: string, data: Record<string, unknown>) => {
      const res = await this.client.post(`/lesson-plans/quizzes/${quizId}/questions`, data);
      return res.data;
    },
    updateQuestion: async (questionId: string, data: Record<string, unknown>) => {
      const res = await this.client.patch(`/lesson-plans/questions/${questionId}`, data);
      return res.data;
    },
    deleteQuestion: async (questionId: string) => {
      const res = await this.client.delete(`/lesson-plans/questions/${questionId}`);
      return res.data;
    },
    submitAttempt: async (quizId: string, answers: Record<string, unknown>) => {
      const res = await this.client.post(`/lesson-plans/quizzes/${quizId}/attempts`, { answers });
      return res.data;
    },
    listAttempts: async (quizId: string) => {
      const res = await this.client.get(`/lesson-plans/quizzes/${quizId}/attempts`);
      return res.data;
    },
    getAttempt: async (attemptId: string) => {
      const res = await this.client.get(`/lesson-plans/attempts/${attemptId}`);
      return res.data;
    },
    gradeAttempt: async (attemptId: string, score: number) => {
      const res = await this.client.post(`/lesson-plans/attempts/${attemptId}/grade`, { score });
      return res.data;
    },
    getMyAttempts: async () => {
      const res = await this.client.get('/lesson-plans/my-attempts');
      return res.data;
    },
  };

  // ─── Dashboard Chart Methods (flat) ───

  async globalSearch(query: string) {
    const res = await this.client.get('/search', { params: { q: query } });
    return res.data.data ?? [];
  }

  async getAttendanceTrend(days: number) {
    const res = await this.client.get('/dashboard/advanced/attendance-trend', {
      params: { months: String(days) },
    });
    return res.data.data ?? [];
  }

  async getFeeCollectionTrend(months: number) {
    const res = await this.client.get('/dashboard/advanced/fee-trend', {
      params: { months: String(months) },
    });
    return res.data.data ?? [];
  }

  async getFeeStatus() {
    const res = await this.client.get('/dashboard/advanced/kpis');
    const d = res.data.data ?? {};
    return { totalCollected: d.totalFeeCollected ?? 0, totalOutstanding: d.totalFeeOutstanding ?? 0 };
  }

  async getClassStrength() {
    const res = await this.client.get('/dashboard/advanced/student-strength');
    return res.data.data ?? [];
  }

  async getGenderDistribution() {
    const res = await this.client.get('/reports/student-strength', { params: { groupBy: 'gender' } });
    return res.data.data ?? [];
  }

  async getTeacherAttendanceTrend(days: number) {
    const res = await this.client.get('/dashboard/advanced/attendance-trend', {
      params: { months: String(days), scope: 'teacher' },
    });
    return res.data.data ?? [];
  }

  async getTeacherAssignmentSubmissions() {
    const res = await this.client.get('/reports/enhanced/assignment-submissions');
    return res.data.data ?? [];
  }

  async getStudentAttendanceSummary() {
    const res = await this.client.get('/attendance/my-summary');
    return res.data.data ?? { present: 0, absent: 0, late: 0 };
  }

  async getStudentExamResults() {
    const res = await this.client.get('/exams/my-results');
    return res.data.data ?? [];
  }
}

export const api = new ApiClient();
